/**
 * History service — Firestore subscription and pagination for unified history log.
 */

import {
  collection,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { HistoryEvent, HistoryEventType } from '@/types/history';

export function subscribeToViewHistory(
  userId: string,
  projectId: string,
  viewId: string,
  options: {
    pageSize?: number;
    filterType?: HistoryEventType;
    onUpdate: (events: HistoryEvent[]) => void;
    onError?: (error: Error) => void;
  }
): Unsubscribe {
  const pageSize = options.pageSize ?? 50;
  const historyRef = collection(
    db,
    `users/${userId}/projects/${projectId}/views/${viewId}/history`
  );

  let q = options.filterType
    ? query(historyRef, where('type', '==', options.filterType), orderBy('timestamp', 'desc'), limit(pageSize))
    : query(historyRef, orderBy('timestamp', 'desc'), limit(pageSize));

  return onSnapshot(
    q,
    (snapshot) => {
      const events = snapshot.docs.map((doc) => ({
        ...doc.data(),
      })) as HistoryEvent[];
      options.onUpdate(events);
    },
    (error) => {
      console.error('Error in history listener:', error);
      if (options.onError) {
        options.onError(error);
      }
    }
  );
}

export async function loadMoreHistory(
  userId: string,
  projectId: string,
  viewId: string,
  lastTimestamp: Timestamp,
  options?: { pageSize?: number; filterType?: HistoryEventType }
): Promise<HistoryEvent[]> {
  const pageSize = options?.pageSize ?? 50;
  const historyRef = collection(
    db,
    `users/${userId}/projects/${projectId}/views/${viewId}/history`
  );

  let q = options?.filterType
    ? query(historyRef, where('type', '==', options.filterType), orderBy('timestamp', 'desc'), startAfter(lastTimestamp), limit(pageSize))
    : query(historyRef, orderBy('timestamp', 'desc'), startAfter(lastTimestamp), limit(pageSize));

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    ...doc.data(),
  })) as HistoryEvent[];
}
